/**
 * usePeriodInquirySummary
 *
 * The "Wyślij zapytanie" (period-inquiry) submit action, extracted from
 * client-offer-page.tsx so public-offer-page.tsx (public link) can reuse the
 * exact same logic instead of re-implementing it — the two routes must never
 * drift on the submit payload or toast copy (qual-dry-violation finding,
 * CU-86cb4wkxx).
 *
 * The "selected periods differ from default" summary block this hook used
 * to also build was v1-`ClientOfferView`-only UI — client-offer-review-v2's
 * footer bar renders its own equivalent (ClientOfferReviewCustomPeriodsPopover,
 * built from the shared period axis in client-offer-review-shell.tsx), so
 * that half of this hook was retired with v1 rather than kept as dead code.
 *
 * The mutation payload shape differs per route (SSO includes `offerId`, the
 * public link doesn't — the token already scopes it), so this hook takes an
 * already-adapted `submit` callback rather than a mutation hook directly.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@ooh/ui/components/sonner';
import type { OfferPeriodInquiryUnitSelection } from '@ooh/contracts';
import { parseApiError } from '@ooh/contracts';
import { buildInquiryBody } from '../lib/period-selection-utils';
import { apiErrorToUserMessage } from '../../../lib/api-error-messages';

export interface UsePeriodInquirySummaryArgs {
  selectedPeriods: Record<string, string[]>;
  /** Already adapted to the route's mutation payload shape (SSO adds offerId, public doesn't). */
  submit: (
    selection: OfferPeriodInquiryUnitSelection[],
    callbacks: { onSuccess: () => void; onError: (error: unknown) => void },
  ) => void;
}

export function usePeriodInquirySummary({ selectedPeriods, submit }: UsePeriodInquirySummaryArgs) {
  const { t } = useTranslation('offers');

  const submitPeriodInquiry = React.useCallback(() => {
    submit(buildInquiryBody(selectedPeriods), {
      onSuccess: () => {
        toast.success(t('clientOffer.inquiry.success'));
      },
      onError: (error: unknown) => {
        toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation'));
      },
    });
  }, [submit, selectedPeriods, t]);

  return { submitPeriodInquiry };
}
